/**
 * Dots & Boxes AI Player
 *
 * Three difficulty levels:
 * - Easy: random valid line, 500ms delay
 * - Medium: complete boxes when possible, avoid giving boxes, 1s delay
 * - Hard: chain analysis with double-dealing strategy, 2s delay
 */

import {
  PlayerColor,
  Line,
  DotsBoxesBoard,
  DotsBoxesDifficulty,
  GridSize,
} from './types';
import { DotsBoxesEngine } from './DotsBoxesEngine';

export class DotsBoxesAI {
  private difficulty: DotsBoxesDifficulty;
  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(difficulty: DotsBoxesDifficulty = 'medium') {
    this.difficulty = difficulty;
  }

  getDelay(): number {
    switch (this.difficulty) {
      case 'easy': return 500;
      case 'medium': return 1000;
      case 'hard': return 2000;
      default: return 1000;
    }
  }

  setDifficulty(difficulty: DotsBoxesDifficulty): void {
    this.difficulty = difficulty;
  }

  getDifficulty(): DotsBoxesDifficulty {
    return this.difficulty;
  }

  /**
   * Choose the best move for the AI.
   */
  chooseMove(
    board: DotsBoxesBoard,
    gridSize: GridSize,
    validMoves: Line[]
  ): Line | null {
    if (validMoves.length === 0) return null;

    switch (this.difficulty) {
      case 'easy':
        return this.chooseMoveEasy(validMoves);
      case 'medium':
        return this.chooseMoveMedium(board, gridSize, validMoves);
      case 'hard':
        return this.chooseMoveHard(board, gridSize, validMoves);
      default:
        return this.chooseMoveEasy(validMoves);
    }
  }

  /**
   * Schedule an AI move after the configured delay.
   */
  scheduleMove(
    board: DotsBoxesBoard,
    gridSize: GridSize,
    validMoves: Line[],
    onMove: (line: Line | null) => void
  ): () => void {
    this.cancelPendingMove();

    const delay = this.getDelay();
    this.pendingTimeout = setTimeout(() => {
      this.pendingTimeout = null;
      const move = this.chooseMove(board, gridSize, validMoves);
      onMove(move);
    }, delay);

    return () => this.cancelPendingMove();
  }

  cancelPendingMove(): void {
    if (this.pendingTimeout !== null) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
  }

  destroy(): void {
    this.cancelPendingMove();
  }

  // =========================================================================
  // Easy: Random valid line
  // =========================================================================

  private chooseMoveEasy(validMoves: Line[]): Line {
    return validMoves[Math.floor(Math.random() * validMoves.length)];
  }

  // =========================================================================
  // Medium: Complete boxes > avoid giving boxes > minimize damage
  // =========================================================================

  private chooseMoveMedium(
    board: DotsBoxesBoard,
    gridSize: GridSize,
    validMoves: Line[]
  ): Line {
    // 1. Find moves that complete a box (3-sided boxes)
    const completingMoves = this.getCompletingMoves(board, gridSize, validMoves);
    if (completingMoves.length > 0) {
      // Pick the one that completes the most boxes
      completingMoves.sort((a, b) => b.boxesCompleted - a.boxesCompleted);
      return completingMoves[0].line;
    }

    // 2. Find safe moves (don't create a 3rd side on any box)
    const safeMoves = this.getSafeMoves(board, gridSize, validMoves);
    if (safeMoves.length > 0) {
      return safeMoves[Math.floor(Math.random() * safeMoves.length)];
    }

    // 3. All moves give away boxes — pick the one that gives away the fewest
    return this.getLeastDamagingMove(board, gridSize, validMoves);
  }

  // =========================================================================
  // Hard: Chain analysis with double-dealing strategy
  // =========================================================================

  private chooseMoveHard(
    board: DotsBoxesBoard,
    gridSize: GridSize,
    validMoves: Line[]
  ): Line {
    // 1. Complete any available boxes first (greedy capture)
    const completingMoves = this.getCompletingMoves(board, gridSize, validMoves);
    if (completingMoves.length > 0) {
      // Capture all available boxes by picking the best completing move
      completingMoves.sort((a, b) => b.boxesCompleted - a.boxesCompleted);
      return completingMoves[0].line;
    }

    // 2. Find safe moves
    const safeMoves = this.getSafeMoves(board, gridSize, validMoves);
    if (safeMoves.length > 0) {
      // Among safe moves, prefer moves that don't open up chains
      return this.getBestSafeMove(board, gridSize, safeMoves);
    }

    // 3. All moves give away boxes — use chain strategy
    // Give away the shortest chain, keeping long chains for later
    return this.getChainStrategyMove(board, gridSize, validMoves);
  }

  // =========================================================================
  // Shared Helpers
  // =========================================================================

  /**
   * Find moves that complete at least one box.
   */
  private getCompletingMoves(
    board: DotsBoxesBoard,
    gridSize: GridSize,
    validMoves: Line[]
  ): { line: Line; boxesCompleted: number }[] {
    const results: { line: Line; boxesCompleted: number }[] = [];

    for (const line of validMoves) {
      const adjacentBoxes = DotsBoxesEngine.getAdjacentBoxes(line, gridSize);
      let completed = 0;

      for (const box of adjacentBoxes) {
        if (board.boxes[box.row][box.col] !== null) continue;
        const sides = DotsBoxesEngine.countBoxSides(board, box.row, box.col, gridSize);
        if (sides === 3) {
          completed++;
        }
      }

      if (completed > 0) {
        results.push({ line, boxesCompleted: completed });
      }
    }

    return results;
  }

  /**
   * Find moves that don't create a 3rd side on any unclaimed box.
   */
  private getSafeMoves(
    board: DotsBoxesBoard,
    gridSize: GridSize,
    validMoves: Line[]
  ): Line[] {
    return validMoves.filter(line => {
      const adjacentBoxes = DotsBoxesEngine.getAdjacentBoxes(line, gridSize);
      for (const box of adjacentBoxes) {
        if (board.boxes[box.row][box.col] !== null) continue;
        const sides = DotsBoxesEngine.countBoxSides(board, box.row, box.col, gridSize);
        if (sides === 2) {
          // Drawing this line would make it 3 sides — opponent can complete it
          return false;
        }
      }
      return true;
    });
  }

  /**
   * Among moves that all give away boxes, pick the one that gives away the fewest.
   */
  private getLeastDamagingMove(
    board: DotsBoxesBoard,
    gridSize: GridSize,
    validMoves: Line[]
  ): Line {
    let bestMove = validMoves[0];
    let minDamage = Infinity;

    for (const line of validMoves) {
      const damage = this.countBoxesGivenAway(board, gridSize, line);
      if (damage < minDamage) {
        minDamage = damage;
        bestMove = line;
      }
    }

    return bestMove;
  }

  /**
   * Count how many boxes would become completable by the opponent after drawing this line.
   */
  private countBoxesGivenAway(
    board: DotsBoxesBoard,
    gridSize: GridSize,
    line: Line
  ): number {
    const adjacentBoxes = DotsBoxesEngine.getAdjacentBoxes(line, gridSize);
    let count = 0;

    for (const box of adjacentBoxes) {
      if (board.boxes[box.row][box.col] !== null) continue;
      const sides = DotsBoxesEngine.countBoxSides(board, box.row, box.col, gridSize);
      if (sides === 2) {
        // This line would be the 3rd side, giving opponent a chance to complete
        count++;
      }
    }

    return count;
  }

  /**
   * Among safe moves, prefer ones that are more central or strategic.
   */
  private getBestSafeMove(
    board: DotsBoxesBoard,
    gridSize: GridSize,
    safeMoves: Line[]
  ): Line {
    // Prefer moves adjacent to boxes with 0 sides (less likely to create chains)
    let bestMove = safeMoves[0];
    let bestScore = -Infinity;

    for (const line of safeMoves) {
      let score = 0;
      const adjacentBoxes = DotsBoxesEngine.getAdjacentBoxes(line, gridSize);

      for (const box of adjacentBoxes) {
        if (board.boxes[box.row][box.col] !== null) continue;
        const sides = DotsBoxesEngine.countBoxSides(board, box.row, box.col, gridSize);
        // Prefer adding to boxes with 0 sides (safest)
        if (sides === 0) score += 2;
        else if (sides === 1) score += 1;
        // sides === 2 shouldn't happen (filtered by getSafeMoves)
      }

      if (score > bestScore) {
        bestScore = score;
        bestMove = line;
      }
    }

    return bestMove;
  }

  /**
   * Chain strategy: when all moves give away boxes, sacrifice the shortest chain.
   * This is the "double-dealing" strategy from competitive Dots & Boxes.
   */
  private getChainStrategyMove(
    board: DotsBoxesBoard,
    gridSize: GridSize,
    validMoves: Line[]
  ): Line {
    // Find chains of 3-sided boxes
    const chains = this.findChains(board, gridSize);

    if (chains.length === 0) {
      // Fallback: least damaging move
      return this.getLeastDamagingMove(board, gridSize, validMoves);
    }

    // Sort chains by length (shortest first)
    chains.sort((a, b) => a.length - b.length);

    // Sacrifice the shortest chain — draw a line that opens it
    const shortestChain = chains[0];

    // Find a move that opens the shortest chain
    for (const line of validMoves) {
      const adjacentBoxes = DotsBoxesEngine.getAdjacentBoxes(line, gridSize);
      for (const box of adjacentBoxes) {
        if (shortestChain.some(cb => cb.row === box.row && cb.col === box.col)) {
          return line;
        }
      }
    }

    // Fallback
    return this.getLeastDamagingMove(board, gridSize, validMoves);
  }

  /**
   * Find chains of connected boxes that have 2 sides drawn.
   * A chain is a sequence of adjacent 2-sided boxes.
   */
  private findChains(
    board: DotsBoxesBoard,
    gridSize: GridSize
  ): { row: number; col: number }[][] {
    const boxCount = gridSize - 1;
    const visited: boolean[][] = Array.from({ length: boxCount }, () =>
      new Array(boxCount).fill(false)
    );
    const chains: { row: number; col: number }[][] = [];

    for (let row = 0; row < boxCount; row++) {
      for (let col = 0; col < boxCount; col++) {
        if (visited[row][col]) continue;
        if (board.boxes[row][col] !== null) continue;

        const sides = DotsBoxesEngine.countBoxSides(board, row, col, gridSize);
        if (sides !== 2) continue;

        // BFS to find connected 2-sided boxes
        const chain: { row: number; col: number }[] = [];
        const queue: { row: number; col: number }[] = [{ row, col }];
        visited[row][col] = true;

        while (queue.length > 0) {
          const current = queue.shift()!;
          chain.push(current);

          // Check 4 neighbors
          const neighbors = [
            { row: current.row - 1, col: current.col },
            { row: current.row + 1, col: current.col },
            { row: current.row, col: current.col - 1 },
            { row: current.row, col: current.col + 1 },
          ];

          for (const n of neighbors) {
            if (n.row < 0 || n.row >= boxCount || n.col < 0 || n.col >= boxCount) continue;
            if (visited[n.row][n.col]) continue;
            if (board.boxes[n.row][n.col] !== null) continue;

            const nSides = DotsBoxesEngine.countBoxSides(board, n.row, n.col, gridSize);
            if (nSides === 2) {
              // Check if they share an undrawn edge (connected)
              if (this.boxesShareUndrawnEdge(board, current, n, gridSize)) {
                visited[n.row][n.col] = true;
                queue.push(n);
              }
            }
          }
        }

        if (chain.length > 0) {
          chains.push(chain);
        }
      }
    }

    return chains;
  }

  /**
   * Check if two adjacent boxes share an undrawn edge between them.
   */
  private boxesShareUndrawnEdge(
    board: DotsBoxesBoard,
    a: { row: number; col: number },
    b: { row: number; col: number },
    _gridSize: GridSize
  ): boolean {
    if (a.row === b.row) {
      // Horizontally adjacent
      const leftCol = Math.min(a.col, b.col);
      // The shared edge is a vertical line at (a.row, leftCol + 1)
      return !board.verticalLines[a.row]?.[leftCol + 1];
    } else if (a.col === b.col) {
      // Vertically adjacent
      const topRow = Math.min(a.row, b.row);
      // The shared edge is a horizontal line at (topRow + 1, a.col)
      return !board.horizontalLines[topRow + 1]?.[a.col];
    }
    return false;
  }
}
